
import Vue, { PropType } from 'vue';
import { get } from 'lodash';
import Api from '@/core/api';
import { Unsubscriber } from '@/core/event-bus';
import { PlayerState } from '@/core/player-state';
import { QnaConfig, Question } from './qna.interface';
import Profile from '@/common/profile/Profile.vue';
import Badge from '@/common/badge/Badge.vue';
import IconButton from '@/common/icon-button/IconButton.vue';
import LikeButton from '@/common/like-button/LikeButton.vue';
import { Icon } from '@/common/icon/Icon.vue';

export default Vue.extend({
    name: 'Qna',
    components: {
        IconButton,
        LikeButton,
        Profile,
        Badge
    },
    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<QnaConfig>,
            default: () => ({} as QnaConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            question: undefined as Question | undefined,
            isVideoElementFullScreen: false,
            profilePic: null,
            unsubscribers: [] as Unsubscriber[],
            collapsed: false,
            chevronUp: Icon.chevronUp,
            chevronDown: Icon.chevronDown,
        };
    },

    computed: {
        isLarge(): boolean {
            return this.config.large || false;
        },

        upvotingEnabled(): boolean {
            return this.config.upvotingEnabled || false;
        },

        upvotingAllowed(): boolean {
            return this.config.upvotingAllowed || false;
        },

        userId(): string {
            return get(this, 'question.pid', '');
        },

        userName(): string {
            return get(this, 'question.participant', '');
        },

        questionId(): string {
            return get(this, 'question.id', '');
        },

        profilePicture(): string {
            return get(this, 'question.profile_pic', '');
        },

        showUserInitials():boolean {
            return get(this, 'question.showUserInitials', true);
        },

        color(): string {
            return get(this, 'config.color');
        },
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(this.onStateChange.bind(this));

        this.unsubscribers.push(
            unsubscriber,
            this.api.onMetadataChange(this.onMetadataChange.bind(this)),
            this.api.onClientAction(this.onClientQuestion.bind(this))
        );

        this.onStateChange(state);
    },

    beforeDestroy(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }
    },

    methods: {
        onStateChange(state: Partial<PlayerState>) {
            this.isVideoElementFullScreen = get(state, 'isVideoElementFullScreen', this.isVideoElementFullScreen);
        },

        onMetadataChange(payload: unknown) {
            if (!get(this, 'config.useMetadata')) return;

            this.api.customAction(get(payload, 'value.qna'), 'qna');
        },

        onClientQuestion(payload: unknown, context?: string) {
            if (context !== 'qna') return;

            this.question = payload as Question;
        },

        toggleCollapse() {
            this.collapsed = !this.collapsed;
        },

        onLikeButtonClick() {
            if (!this.question) {
                return;
            }
            const action = this.question.upvoted ? 'removeQuestionUpvote' : 'addQuestionUpvote';
            this.api.customAction(this.question, action);
        }
    }
});
